import {
  Formik,
  FormikInsurance,
  Nationality,
  YesNoAnswersNew,
} from "src/types";
import { InputChip, InputDate, InputSelect, InputText } from "src/ui/widgets";
import { useNaturalContractor } from "./natural-contractor.hook";
import { lowerLanguage } from "src/i18n";
import PhoneInput from "react-phone-input-2";
import { AnyObjectSchema } from "yup";
import { ChangeEvent } from "react";

export function NaturalContractor({
  validationSchema,
  formik,
}: {
  formik: Formik<FormikInsurance>;
  validationSchema: AnyObjectSchema;
}) {
  const { handleChange, handleBlur, setFieldValue, setFieldTouched } = formik;

  const {
    naturalContractor,
    naturalTouched,
    naturalErrors,
    nationalityOptions,
    fcQuestionOptions,
    genderOptions,
    contractorIsInsuredQuestionOptions,
    provincesOptions,
    residenceMunicipalitiesOptions,
    capOptions,
    t,
    onChangePhone,
    onSelectFcQuestion,
    onSelectResidenceProvince,
    onSelectResidenceMunicipality,
    onSelectContractorCap,
  } = useNaturalContractor({ formik });

  return (
    <>
      <div className="d-block d-md-flex">
        <div className="col-sm-12 col-md-6 p-0">
          <InputChip
            name="contractor.contractorIsInsuredQuestion"
            handleChange={handleChange}
            handleBlur={handleBlur}
            options={contractorIsInsuredQuestionOptions}
            validationSchema={validationSchema}
            touched={naturalTouched.contractorIsInsuredQuestion}
            value={naturalContractor.contractorIsInsuredQuestion}
            error={naturalErrors.contractorIsInsuredQuestion}
            label={t("data_entry_step_2.contractor_insured_question")}
            classesLabel="m-0 p-0 font-16 d-flex align-items-center mb-2"
            classesOption="py-1"
            classes="col-7 mb-4"
          />
        </div>
      </div>
      <div className="d-block d-md-flex">
        <div className="col-sm-12 col-md-6 p-0">
          <InputChip
            name="contractor.contractorNationality"
            handleChange={handleChange}
            handleBlur={handleBlur}
            options={nationalityOptions}
            validationSchema={validationSchema}
            touched={naturalTouched.contractorNationality}
            value={naturalContractor.contractorNationality}
            error={naturalErrors.contractorNationality}
            label={t("data_entry_step_2.insured_nationality")}
            classesOption="py-1"
            classes="col-7 mb-4"
            classesLabel="m-0 p-0 font-16 d-flex align-items-center mb-2"
          />
        </div>
      </div>
      <div className="d-block d-md-flex">
        <div className={`col-sm-12 col-md-6 p-0 pe-md-3`}>
          <InputText
            name="contractor.contractorName"
            handleChange={handleChange}
            handleBlur={handleBlur}
            validationSchema={validationSchema}
            touched={naturalTouched.contractorName}
            value={naturalContractor.contractorName}
            error={naturalErrors.contractorName}
            label={`${t("data_entry_step_2.insured_name")}*`}
            placeholder={t("data_entry_step_2.insured_name_placeholder")}
          />
        </div>
        <div className={`col-sm-12 col-md-6 p-0 ps-md-3`}>
          <InputText
            name="contractor.contractorSurname"
            handleChange={handleChange}
            handleBlur={handleBlur}
            validationSchema={validationSchema}
            touched={naturalTouched.contractorSurname}
            value={naturalContractor.contractorSurname}
            error={naturalErrors.contractorSurname}
            label={`${t("data_entry_step_2.insured_surname")}*`}
            placeholder={t("data_entry_step_2.insured_surname_placeholder")}
          />
        </div>
      </div>
      <div className="d-block d-md-flex">
        <div className={`col-sm-12 col-md-6 p-0 pe-md-3`}>
          <InputText
            name="contractor.contractorEmail"
            handleChange={handleChange}
            handleBlur={handleBlur}
            validationSchema={validationSchema}
            touched={naturalTouched.contractorEmail}
            value={naturalContractor.contractorEmail}
            error={naturalErrors.contractorEmail}
            label={`${t("data_entry_step_2.insured_email")}*`}
            placeholder={t("data_entry_step_2.insured_email_placeholder")}
          />
        </div>
        <div className={`col-sm-12 col-md-6 p-0 ps-md-3`}>
          <InputText
            name="contractor.contractorEmailConfirm"
            handleChange={handleChange}
            handleBlur={handleBlur}
            validationSchema={validationSchema}
            touched={naturalTouched.contractorEmailConfirm}
            error={naturalErrors.contractorEmailConfirm}
            value={naturalContractor.contractorEmailConfirm}
            label={`${t("data_entry_step_2.insured_email_confirm")}*`}
            placeholder={t(
              "data_entry_step_2.insured_email_confirm_placeholder"
            )}
            onPaste={(event) => {
              event.preventDefault();
              return false;
            }}
            onCopy={(event) => {
              event.preventDefault();
              return false;
            }}
          />
        </div>
      </div>
      <div className="d-block d-md-flex">
        <div className={`col-sm-12 col-md-6 p-0 pe-md-3 mb-2`}>
          <InputSelect
            name="contractor.contractorResidenceProvince"
            setFieldValue={setFieldValue}
            options={provincesOptions}
            validationSchema={validationSchema}
            handleChange={onSelectResidenceProvince}
            touched={naturalTouched.contractorResidenceProvince?.value}
            value={naturalContractor.contractorResidenceProvince.id}
            error={naturalErrors.contractorResidenceProvince?.value}
            label={`${t("data_entry_step_2.insured_residence_province")}*`}
            placeholder={t(
              "data_entry_step_2.insured_residence_province_placeholder"
            )}
          />
        </div>
        <div className={` col-sm-12 col-md-6 p-0 ps-md-3 mb-2`}>
          <InputSelect
            name="contractor.contractorResidenceMunicipality"
            setFieldValue={setFieldValue}
            options={residenceMunicipalitiesOptions}
            disabled={!naturalContractor.contractorResidenceProvince.value}
            handleChange={onSelectResidenceMunicipality}
            validationSchema={validationSchema}
            touched={naturalTouched.contractorResidenceMunicipality?.value}
            value={naturalContractor.contractorResidenceMunicipality.id}
            error={naturalErrors.contractorResidenceMunicipality?.value}
            label={`${t("data_entry_step_2.insured_residence_municipality")}*`}
            placeholder={t(
              "data_entry_step_2.insured_residence_municipality_placeholder"
            )}
          />
        </div>
      </div>
      <div className="d-block d-md-flex">
        <div className={`col-sm-12 col-md-6 p-0 pe-md-3`}>
          <InputText
            name="contractor.contractorRedisenceAddress"
            handleChange={handleChange}
            handleBlur={handleBlur}
            validationSchema={validationSchema}
            touched={naturalTouched.contractorRedisenceAddress}
            value={naturalContractor.contractorRedisenceAddress}
            error={naturalErrors.contractorRedisenceAddress}
            label={`${t("data_entry_step_2.insured_residence_address")}*`}
            placeholder={t(
              "data_entry_step_2.insured_residence_address_placeholder"
            )}
          />
        </div>
        <div className={`col-sm-12 col-md-6 p-0 ps-md-3`}>
          <InputSelect
            name="contractor.contractorCap"
            setFieldValue={setFieldValue}
            handleChange={onSelectContractorCap}
            options={capOptions}
            validationSchema={validationSchema}
            touched={naturalTouched.contractorCap?.value}
            value={naturalContractor.contractorCap.id}
            error={naturalErrors.contractorCap?.value}
            disabled={
              !naturalContractor.contractorResidenceProvince.value ||
              !naturalContractor.contractorResidenceMunicipality.value
            }
            label={`${t("data_entry_step_2.insured_cap")}*`}
            placeholder={t("data_entry_step_2.insured_cap_placeholder")}
          />
        </div>
      </div>
      <div className="d-block d-md-flex">
        <div
          id="input-contractor.contractorPhone"
          className={`${
            !naturalTouched.contractorPhone?.formattedPhone ||
            !naturalTouched.contractorPhone?.formattedPhone
              ? "mb-4"
              : "mb-3"
          } col-sm-12 col-md-6 p-0 pe-md-3`}
        >
          <label
            htmlFor="input-insuredPhone"
            className="input-text--label d-block mb-md-2 mb-1"
          >
            {`${t("data_entry_step_2.insured_phone")}*`}
          </label>
          <div
            className={
              naturalTouched.contractorPhone?.formattedPhone &&
              naturalErrors.contractorPhone?.formattedPhone
                ? "phone-style-error"
                : "phone-style"
            }
          >
            <PhoneInput
              country={
                lowerLanguage === "it"
                  ? "it"
                  : lowerLanguage === "en"
                  ? "gb"
                  : "de"
              }
              placeholder={t("data_entry_step_2.insured_phone_placeholder")}
              value={naturalContractor.contractorPhone?.formattedPhone}
              onChange={onChangePhone}
              onBlur={() =>
                setFieldTouched(
                  "contractor.contractorPhone.formattedPhone",
                  true
                )
              }
              inputProps={{
                id: "input-insuredPhone",
                name: "contractorPhone.formattedPhone",
                autoComplete: "tel",
                inputMode: "tel",
              }}
            />
          </div>
          {naturalTouched.contractorPhone?.formattedPhone &&
            naturalErrors.contractorPhone?.formattedPhone && (
              <div className="font-14 c-error">
                {naturalErrors.contractorPhone?.formattedPhone}
              </div>
            )}
        </div>
      </div>

      <div className="d-block d-md-flex">
        <div className="col-sm-12 col-md-6 p-0 pe-md-3 mb-3">
          <InputChip
            name="contractor.contractorFcQuestion"
            handleChange={(event: ChangeEvent<HTMLInputElement>) => {
              onSelectFcQuestion(event);
              handleChange(event);
            }}
            handleBlur={handleBlur}
            options={fcQuestionOptions}
            validationSchema={validationSchema}
            touched={naturalTouched.contractorFcQuestion}
            value={naturalContractor.contractorFcQuestion}
            error={naturalErrors.contractorFcQuestion}
            label={`${t("data_entry_step_2.insured_fc_question")}`}
            classesOption="py-1"
            classes="col-7 mb-3"
            classesLabel="m-0 p-0 font-16 d-flex align-items-center mb-2"
          />
        </div>
      </div>
      {naturalContractor.contractorFcQuestion === YesNoAnswersNew.YES && (
        <div className="d-block d-md-flex">
          <div className="col-sm-12 col-md-6 p-0 pe-md-3">
            <InputText
              name="contractor.contractorFc"
              handleChange={handleChange}
              handleBlur={handleBlur}
              validationSchema={validationSchema}
              touched={naturalTouched.contractorFc}
              value={naturalContractor.contractorFc?.toUpperCase()}
              error={naturalErrors.contractorFc}
              label={`${t("data_entry_step_2.insured_fc")}*`}
              placeholder={t("data_entry_step_2.insured_fc_placeholder")}
            />
          </div>
        </div>
      )}
      {naturalContractor.contractorFcQuestion === YesNoAnswersNew.NO && (
        <div>
          <div className="d-block d-md-flex">
            <div className="col-sm-12 col-md-6 p-0 pe-md-3">
              <InputDate
                name="contractor.contractorBirth"
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                validationSchema={validationSchema}
                touched={naturalTouched.contractorBirth}
                value={naturalContractor.contractorBirth}
                error={naturalErrors.contractorBirth}
                label={`${t("data_entry_step_2.insured_birth")}*`}
                placeholder={t("data_entry_step_2.insured_birth_placeholder")}
                disabledTo={new Date()}
                classes="m-0 p-0"
              />
            </div>

            <div className="col-sm-12 col-md-6 p-0 ps-md-3">
              <InputText
                name="contractor.contractorBirthNation"
                handleChange={handleChange}
                handleBlur={handleBlur}
                validationSchema={validationSchema}
                touched={naturalTouched.contractorBirthNation}
                value={naturalContractor.contractorBirthNation}
                error={naturalErrors.contractorBirthNation}
                label={`${t("data_entry_step_2.insured_birth_nation")}*`}
                placeholder={t(
                  "data_entry_step_2.insured_birth_nation_placeholder"
                )}
              />
            </div>
          </div>

          <div className="d-block d-md-flex">
            <div className="col-sm-12 col-md-6 p-0 pe-md-3">
              <InputText
                name="contractor.contractorBirthPlace"
                handleChange={handleChange}
                handleBlur={handleBlur}
                validationSchema={validationSchema}
                touched={naturalTouched.contractorBirthPlace}
                value={naturalContractor.contractorBirthPlace}
                error={naturalErrors.contractorBirthPlace}
                label={`${t("data_entry_step_2.insured_birth_place")}*`}
                placeholder={t(
                  "data_entry_step_2.insured_birth_place_placeholder"
                )}
              />
            </div>
            <div className="col-sm-12 col-md-6 p-0 ps-md-3">
              <InputText
                name="contractor.contractorBirthProvince"
                handleChange={handleChange}
                handleBlur={handleBlur}
                validationSchema={validationSchema}
                touched={naturalTouched.contractorBirthProvince}
                value={naturalContractor.contractorBirthProvince}
                error={naturalErrors.contractorBirthProvince}
                label={`${t("data_entry_step_2.insured_birth_province")}*`}
                placeholder={t(
                  "data_entry_step_2.insured_birth_province_placeholder"
                )}
              />
            </div>
          </div>

          <div className="d-block d-md-flex">
            <div className="col-sm-12 col-md-6 p-0 pe-md-3">
              <InputChip
                name="contractor.contractorGender"
                handleChange={handleChange}
                handleBlur={handleBlur}
                options={genderOptions}
                validationSchema={validationSchema}
                touched={naturalTouched.contractorGender}
                value={naturalContractor.contractorGender}
                error={naturalErrors.contractorGender}
                label={`${t("data_entry_step_2.insured_gender")}*`}
                classesLabel="m-0 p-0 font-16 d-flex align-items-center mb-3"
                classesOption="py-1"
                classes="col-7 mb-4"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
